import { isEmpty } from 'lodash';
import moment, { MomentInput } from 'moment';
import { DEFAULT_DATE_FORMAT as API_DATE_FORMAT } from 'api/models';
import { ProjectPhase } from 'api/project';
import { Task } from 'api/tasks';
import { logger } from 'services';

const formatDate = (date: MomentInput, format?: string): MomentInput =>
  moment(date).format(format ?? API_DATE_FORMAT);

export enum EntityType {
  'Phase',
  'Task',
}
export enum DateType {
  StartDate = 'startDate',
  DueDate = 'dueDate',
}

export type Entity = {
  id: number;
  startDate?: MomentInput;
  dueDate?: MomentInput;
  parent?: number;
  children?: number[];
  dependentOn?: number[];
  dependencyFor?: number[];
  type: EntityType;
  name?: string;
  depth: number;
  startDateVerified?: boolean;
  dueDateVerified?: boolean;
  dependenciesVerified?: number[];
  hasStartDate?: boolean;
  hasDueDate?: boolean;
  projectPhaseId?: number;
};

export type List = {
  [id: number]: Entity;
};

export type Updates = {
  [id: number]: Pick<
    Entity,
    'id' | 'type' | 'startDate' | 'dueDate' | 'dependentOn' | 'dependencyFor'
  >;
};

export type EntityDateShiftUpdate = Pick<
  Entity,
  'id' | 'startDate' | 'dueDate' | 'dependentOn' | 'dependencyFor'
>;

export type DateShiftUpdates = {
  [id: number]: EntityDateShiftUpdate;
};

export type DateShiftUpdatesForPhase = Required<
  Pick<ProjectPhase, 'projectPhaseId'>
> &
  Partial<Pick<ProjectPhase, 'startDate' | 'dueDate'>>;

export type DateShiftUpdatesForTask = Required<Pick<Task, 'taskId'>> &
  Partial<Pick<Task, 'startDate' | 'dueDate' | 'dependentTasks'>>;

type Options = {
  updates?: Updates;
  allowWeekend?: boolean;
  alignPhaseWithTask?: boolean;
  alignParentWithChildren?: boolean;
};

type EntityDateDiff = {
  startDate: number;
  dueDate: number;
  duration: number;
};

type HandleShiftOptions = {
  processDependenciesFor?: boolean;
  processDependentsOn?: boolean;
};

export const isWeekEnd = (date: MomentInput) =>
  [0, 6].includes(moment(date).day());

/**
 * moment `weeksInYear` util is available in 2.6
 * while the current moment version is at 2.29
 * hence created the following util to avoid version upgrade.
 */
const getWeeksInYear = (date: MomentInput) => {
  let _date = moment(date).endOf('year');
  const year = moment(date).year();

  /**
   * Because the first day of the first week does not always fall on the first day of the year,
   * sometimes the week-year will differ from the month year.
   * For example, in the US, the week that contains Jan 1 is always the first week.
   * In the US, weeks also start on Sunday.
   * If Jan 1 was a Monday, Dec 31 would belong to the same week as Jan 1, and thus the same week-year as Jan 1.
   * Dec 30 would have a different week-year than Dec 31.
   * Ref: https://momentjs.com/docs/#/get-set/week-year/
   */
  while (_date.weekYear() !== year) {
    _date = moment(_date).subtract(1, 'd');
  }

  return _date.week();
};

const getDateDiff = (_date1: MomentInput, _date2: MomentInput): number => {
  return moment(formatDate(_date1)).diff(moment(formatDate(_date2)), 'days');
};

export const getDaysDiff = (
  _date1: MomentInput,
  _date2: MomentInput,
  allowWeekend?: boolean,
): number => {
  if (allowWeekend) {
    return Math.round(
      moment(_date1)
        .endOf('day')
        .diff(moment(_date2).startOf('day'), 'days', true),
    );
  }
  const date1 = moment(_date1);
  const date2 = moment(_date2);
  let daysDiff = Math.round(date1.diff(date2, 'days', true));
  let week1 = date1.week();
  let week2 = date2.week();

  /** Year end handling */
  if (date1.isAfter(date2) && week1 < week2) {
    week1 = getWeeksInYear(date2) + week1;
  } else if (date2.isAfter(date1) && week2 < week1) {
    week2 = getWeeksInYear(date1) + week2;
  }

  const weekDiff = Math.abs(week2 - week1);
  const direction = daysDiff < 0 ? -1 : 1;

  daysDiff = Math.abs(daysDiff);

  if (weekDiff > 0) {
    if (isWeekEnd(date1) || isWeekEnd(date2)) {
      daysDiff -= (weekDiff - 1) * 2;
    } else {
      daysDiff -= weekDiff * 2;
    }
  }

  return daysDiff * direction;
};

export const addDays = (date, daysCount, allowWeekend?: boolean) => {
  if (allowWeekend) {
    return moment(date).add(daysCount - 1, 'day');
  }

  let newDate = moment(date);
  const direction = daysCount < 0 ? -1 : 1;
  let _daysCount = Math.abs(daysCount);

  if ([0, 6].includes(moment(date).day())) {
    let tempNewDate = moment(newDate);

    while (
      _daysCount > 0 &&
      [0, 6].includes(tempNewDate.add(direction, 'day').day())
    ) {
      tempNewDate = moment(newDate);
      _daysCount -= 1;
      newDate = newDate.add(direction, 'day');
    }
  }

  while (_daysCount > 0) {
    const tempNewDate = moment(newDate);

    if (![0, 6].includes(tempNewDate.add(direction, 'day').day())) {
      _daysCount -= 1;
    }
    newDate = newDate.add(direction, 'day');
  }

  return newDate;
};

export const subtractDays = (date, daysCount) => {
  let newDate = moment(date);
  const direction = daysCount < 0 ? -1 : 1;
  let _daysCount = Math.abs(daysCount);

  if ([0, 6].includes(moment(date).day())) {
    let tempNewDate = moment(newDate);

    while (
      _daysCount > 0 &&
      [0, 6].includes(tempNewDate.subtract(direction, 'day').day())
    ) {
      tempNewDate = moment(newDate);
      _daysCount -= 1;
      newDate = newDate.subtract(direction, 'day');
    }
  }

  while (_daysCount > 0) {
    const tempNewDate = moment(newDate);

    if (![0, 6].includes(tempNewDate.subtract(direction, 'day').day())) {
      _daysCount -= 1;
    }
    newDate = newDate.subtract(direction, 'day');
  }

  return newDate;
};

export const getDuration = (
  _startDate: MomentInput,
  _dueDate: MomentInput,
  allowWeekend?: boolean,
): number => {
  if (allowWeekend) {
    return getDaysDiff(_dueDate, _startDate, allowWeekend);
  }
  let duration = 0;
  let startDate = moment(_startDate);
  const dueDate = moment(_dueDate);

  const startDay = startDate.day();
  const dueDay = dueDate.day();

  if (startDate.isSame(dueDate, 'day')) {
    return 1;
  }

  while (startDate.isSameOrBefore(dueDate, 'day')) {
    if (![0, 6].includes(startDate.day())) {
      duration += 1;
    }
    startDate = startDate.add(1, 'day');
  }
  if (startDay === 0) {
    duration += 1;
  } else if (startDay === 6) {
    duration += 2;
  }

  if (
    [0, 6].includes(dueDay) &&
    (![0, 6].includes(startDay) || dueDate.diff(moment(_startDate), 'days') > 5)
  ) {
    if (dueDay === 6) {
      duration += 1;
    } else if (dueDay === 0) {
      duration += 2;
    }
  }

  return duration;
};

export const getCurrentDuration = (
  id: number,
  _startDate: MomentInput,
  _dueDate: MomentInput,
  childNodes: List,
): number => {
  const countedWeekends = [] as MomentInput[];
  const blackListedWeekends = [] as MomentInput[];

  if (isWeekEnd(_startDate)) {
    if (!blackListedWeekends.includes(_startDate)) {
      blackListedWeekends.push(
        _startDate,
        formatDate(
          moment(_startDate).day() === 6
            ? moment(_startDate).add(1, 'day')
            : moment(_startDate).subtract(1, 'day'),
        ),
      );
    }
  }

  if (isWeekEnd(_dueDate)) {
    if (!blackListedWeekends.includes(_dueDate)) {
      blackListedWeekends.push(
        _dueDate,
        formatDate(
          moment(_dueDate).day() === 6
            ? moment(_dueDate).add(1, 'day')
            : moment(_dueDate).subtract(1, 'day'),
        ),
      );
    }
  }

  Object.values(childNodes)?.forEach((child) => {
    const { startDate, dueDate } = child;

    if (
      isWeekEnd(startDate) &&
      !countedWeekends.includes(startDate) &&
      !blackListedWeekends.includes(startDate)
    ) {
      countedWeekends.push(
        startDate,
        formatDate(
          moment(startDate).day() === 6
            ? moment(startDate).add(1, 'day')
            : moment(startDate).subtract(1, 'day'),
        ),
      );
    }

    if (
      isWeekEnd(dueDate) &&
      !countedWeekends.includes(dueDate) &&
      !blackListedWeekends.includes(dueDate)
    ) {
      countedWeekends.push(
        dueDate,
        formatDate(
          moment(dueDate).day() === 6
            ? moment(dueDate).add(1, 'day')
            : moment(dueDate).subtract(1, 'day'),
        ),
      );
    }
  });

  return getDuration(_startDate, _dueDate) + countedWeekends.length;
};

export const getNewDates = (
  date: string | null,
  duration: number,
  type: DateType,
) => {
  let newStartDate: MomentInput;
  let newDueDate: MomentInput;

  if (type === DateType.StartDate) {
    newStartDate = date;
    newDueDate = formatDate(addDays(newStartDate, duration - 1));
  } else {
    newDueDate = date;
    newStartDate = formatDate(subtractDays(date, duration - 1));
  }

  return [newStartDate, newDueDate];
};

export const getChildNodeIds = (id: number, lookup: List): number[] => {
  const { children } = lookup[id];

  return children && children.length > 0
    ? [
        ...children,
        ...children.reduce((result, childId) => {
          result.push(...getChildNodeIds(childId, lookup));

          return result;
        }, [] as number[]),
      ]
    : [];
};

const shiftDate = (
  currentDate: MomentInput,
  dateDiff: number,
  _allowWeekend?: boolean,
): MomentInput => {
  if (dateDiff === 0) {
    return currentDate;
  }
  // const newDate = _allowWeekend
  //   ? moment(currentDate).add(dateDiff, 'days')
  //   : addDays(currentDate, dateDiff);

  const newDate = addDays(currentDate, dateDiff, _allowWeekend);

  // if (_allowWeekend || ![0, 6].includes(newDate.day())) {
  //   return newDate.format();
  // }

  // const targetDayIndex =
  //   newDate.day() === 6 ? (dateDiff > 0 ? 8 : 5) : dateDiff > 0 ? 1 : -2;

  return newDate.format();
};

export const getDragNewDates = (
  task: Entity,
  dragDistance: number,
  allowWeekend?: boolean,
): [MomentInput, MomentInput] => {
  const { startDate, dueDate } = task;

  const currentDuration = getDuration(startDate, dueDate, allowWeekend);
  const newStartDate = shiftDate(startDate, dragDistance, allowWeekend);
  const newEndDate = addDays(newStartDate, currentDuration - 1, allowWeekend)
    .endOf('day')
    .format();

  return [newStartDate, newEndDate];
};

export const getAutoCorrections = (
  phases: List,
  tasks: List,
  options?: Options,
): [DateShiftUpdates, DateShiftUpdates] => {
  const updates: Updates = {};
  const nodesLookup = { ...phases, ...tasks };
  const allowWeekend = options?.allowWeekend ?? false;

  const getNode = (id) => ({
    ...nodesLookup[id],
    ...updates[id],
  });

  const updateNode = (id: number, nodeUpdates: Partial<Entity>) => {
    updates[id] = {
      ...(updates[id] || { id, type: nodesLookup[id].type }),
      ...nodeUpdates,
    };
  };

  const markStartDateVerified = (id) => {
    nodesLookup[id] = {
      ...nodesLookup[id],
      startDateVerified: true,
    };
  };

  const markDueDateVerified = (id) => {
    nodesLookup[id] = {
      ...nodesLookup[id],
      dueDateVerified: true,
    };
  };

  const isAllNodesVerified = () => {
    return !Object.values(nodesLookup).find(({ id }) => {
      const { startDateVerified, dueDateVerified } = getNode(id);

      return !startDateVerified || !dueDateVerified;
    });
  };

  const updateVerifiedDependencies = (id, dependencyId) => {
    const node = nodesLookup[id];

    nodesLookup[id] = {
      ...node,
      dependenciesVerified: [
        ...(node.dependenciesVerified || []),
        dependencyId,
      ],
    };
  };

  const isAllDependenciesVerified = (id) => {
    const { dependentOn, dependenciesVerified } = getNode(id);

    return dependentOn?.every((dependentId) =>
      dependenciesVerified?.includes(dependentId),
    );
  };

  const getAllChildNodes = (id: number): number[] => {
    const { children } = getNode(id);

    return children && children.length > 0
      ? [
          ...children,
          ...children.reduce((result, childId) => {
            result.push(...getAllChildNodes(childId));

            return result;
          }, [] as number[]),
        ]
      : [];
  };

  const getAllParentNodes = (id: number): number[] => {
    const { parent } = getNode(id);

    return parent ? [parent, ...getAllParentNodes(parent)] : [];
  };

  const getTreeNodes = (id: number): number[] => {
    return [id, ...getAllChildNodes(id), ...getAllParentNodes(id)];
  };

  const isTreeDependentOn = (tree1: number[], tree2: number[]): boolean => {
    const _tree1 = tree1.filter((nodeId) => !tree2.includes(nodeId));
    const _tree2 = tree2.filter((nodeId) => !tree1.includes(nodeId));

    for (let index = 0; index < _tree1.length; index += 1) {
      const { dependentOn } = getNode(_tree1[index]);

      /**
       * If in case dependencies happen to be in the same tree,
       * avoiding such nodes during processing.
       */
      const validDependentOn = dependentOn
        ? dependentOn?.filter((_id) => !_tree1.includes(_id))
        : [];

      if (validDependentOn?.find((_id) => _tree2.includes(_id))) {
        return true;
      }
      const dependencies = validDependentOn || [];

      for (let _index = 0; _index < dependencies?.length; _index += 1) {
        const tree = getTreeNodes(dependencies[_index]);

        if (isTreeDependentOn(tree, _tree2)) {
          return true;
        }
      }
    }

    return false;
  };

  const removeInvalidDependencies = () => {
    const dependentTasks = Object.values(tasks).filter(
      ({ dependentOn }) => dependentOn && dependentOn.length > 0,
    );

    dependentTasks.forEach((_task) => {
      const node = getNode(_task.id);
      const childNodes = getAllChildNodes(_task.id);
      const parentNodes = getAllParentNodes(_task.id);

      node.dependentOn?.forEach((dependencyId) => {
        if (
          childNodes.includes(dependencyId) ||
          parentNodes.includes(dependencyId) ||
          isTreeDependentOn(getTreeNodes(dependencyId), [
            _task.id,
            ...childNodes,
            ...parentNodes,
          ])
        ) {
          updateNode(_task.id, {
            dependentOn: node.dependentOn?.filter(
              (_id) => _id !== dependencyId,
            ),
          });
          const dependency = getNode(dependencyId);

          updateNode(dependencyId, {
            dependencyFor: dependency.dependencyFor?.filter(
              (_id) => _id !== _task.id,
            ),
          });
        }
      });
    });
  };

  const processTree = (rootNodeId, currentDepth) => {
    const treeNodesByDepth: { depth: number; nodes: number[] }[] = [];
    const { depth: rootNodeDepth } = getNode(rootNodeId);
    let iDepth = rootNodeDepth;

    (function topDownTraversal(nodeIds) {
      if (nodeIds.length <= 0) {
        return;
      }
      treeNodesByDepth.push({ depth: iDepth, nodes: nodeIds });
      iDepth += 1;
      const nodesAtCurrentDepth: number[] = [];

      nodeIds.forEach((_id) => {
        const { startDate: parentStartDate, children } = getNode(_id);

        if (!isAllDependenciesVerified(_id)) {
          return;
        }
        markStartDateVerified(_id);
        children?.forEach((childId) => {
          const { startDate: childStartDate, dueDate: childDueDate } =
            getNode(childId);

          if (moment(childStartDate).isBefore(moment(parentStartDate))) {
            const duration = getDaysDiff(
              childDueDate,
              childStartDate,
              allowWeekend,
            );

            updateNode(childId, {
              startDate: parentStartDate,
              dueDate: addDays(parentStartDate, duration, allowWeekend).format(
                'YYYY-MM-DD',
              ),
            });
          }
        });

        nodesAtCurrentDepth.push(...(children || []));
      });

      topDownTraversal(nodesAtCurrentDepth);
    })([rootNodeId]);

    for (let index = treeNodesByDepth.length - 1; index >= 0; index -= 1) {
      const { depth, nodes } = treeNodesByDepth[index];

      if (depth < currentDepth) {
        break;
      }
      nodes.forEach((_id) => {
        const { id, children, dueDate } = getNode(_id);
        const isChildrenDueDateVerified = !children?.find((childId) => {
          const child = getNode(childId);

          return !child.dueDateVerified;
        });

        if (!isChildrenDueDateVerified) {
          return;
        }

        const newDueDate =
          !children || children?.length <= 0
            ? dueDate
            : children?.reduce((currentDate, childId) => {
                const child = getNode(childId);

                if (
                  !currentDate ||
                  moment(child.dueDate).isAfter(moment(currentDate))
                ) {
                  return child.dueDate;
                }

                return currentDate;
              }, dueDate);

        if (dueDate !== newDueDate) {
          updateNode(id, {
            dueDate: newDueDate,
          });
        }
        markDueDateVerified(id);
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        processVerifiedNode(id, currentDepth);
      });
    }
  };

  const processVerifiedNode = (id, currentDepth) => {
    const { dueDate, dependencyFor } = getNode(id);

    dependencyFor?.forEach((dependentId) => {
      const dependency = getNode(dependentId);
      const dependencyParent = getNode(dependency.parent);

      if (moment(dependency.startDate).isSameOrBefore(moment(dueDate))) {
        const duration = getDaysDiff(
          dependency.dueDate,
          dependency.startDate,
          allowWeekend,
        );
        const newStartDate = moment(dueDate)
          .add(1, 'days')
          .format('YYYY-MM-DD');

        updateNode(dependentId, {
          startDate: newStartDate,
          dueDate: addDays(newStartDate, duration, allowWeekend).format(
            'YYYY-MM-DD',
          ),
        });
      }
      updateVerifiedDependencies(dependentId, id);

      if (
        !dependencyParent.startDateVerified ||
        !isAllDependenciesVerified(dependentId)
      ) {
        return;
      }
      markStartDateVerified(dependentId);
      processTree(dependentId, currentDepth);
    });
  };

  /**
   * Step 1: Remove invalid dependencies
   */
  removeInvalidDependencies();

  /**
   * Step 2: Top down tree traversal for start date verification
   */
  const treeDepth = (function processIndependentNodes(currentDepth) {
    const nodesAtCurrentDepth = Object.values(nodesLookup)
      .filter(({ depth }) => depth === currentDepth)
      .map(({ id }) => id);

    if (nodesAtCurrentDepth.length <= 0) {
      return currentDepth;
    }

    const independentNodes = nodesAtCurrentDepth.filter((id) => {
      const { dependentOn } = getNode(id);

      return isEmpty(dependentOn);
    });

    independentNodes.forEach((id) => {
      const { children, startDate: parentStartDate, parent } = getNode(id);
      const parentNode = getNode(parent);

      if (!isEmpty(parentNode) && !parentNode.startDateVerified) {
        return;
      }
      markStartDateVerified(id);
      children?.forEach((childId) => {
        const { startDate: childStartDate, dueDate: childDueDate } =
          getNode(childId);

        if (moment(childStartDate).isBefore(moment(parentStartDate))) {
          const duration = getDaysDiff(
            childDueDate,
            childStartDate,
            allowWeekend,
          );

          updateNode(childId, {
            startDate: parentStartDate,
            dueDate: addDays(parentStartDate, duration, allowWeekend).format(
              'YYYY-MM-DD',
            ),
          });
        }
      });
    });

    return processIndependentNodes(currentDepth + 1);
  })(0);

  /**
   * Step 3: Bottom up tree traversal for due date and dependency verification
   */
  for (let currentDepth = treeDepth - 1; currentDepth >= 0; currentDepth -= 1) {
    const nodesAtCurrentDepth = Object.values(nodesLookup)
      .filter(({ depth }) => depth === currentDepth)
      .map(({ id }) => id);

    const startDateVerifiedNodes = nodesAtCurrentDepth.filter((id) => {
      const { startDateVerified } = getNode(id);

      return startDateVerified;
    });

    startDateVerifiedNodes.forEach((id) => {
      const { children, dueDate, type } = getNode(id);
      const isChildrenDueDateVerified = !children?.find((childId) => {
        const child = getNode(childId);

        return !child.dueDateVerified;
      });

      if (!isChildrenDueDateVerified) {
        return;
      }

      const newDueDate =
        !children || children?.length <= 0
          ? dueDate
          : children?.reduce((currentDate: MomentInput | null, childId) => {
              const child = getNode(childId);

              if (
                !currentDate ||
                moment(child.dueDate).isAfter(moment(currentDate))
              ) {
                return child.dueDate;
              }

              return currentDate;
            }, null);

      if (dueDate !== newDueDate) {
        /**
         * Phase should always be having the earliest start date and latest due date of its children
         * It need not be the case for a parent task where its due date can be latest than due date of its children.
         * The above assertion is based on the product call taken.
         */
        if (
          type !== EntityType.Task ||
          moment(dueDate).isBefore(moment(newDueDate))
        ) {
          updateNode(id, {
            dueDate: newDueDate,
          });
        }
      }
      markDueDateVerified(id);
      processVerifiedNode(id, currentDepth);
    });
  }

  if (!isAllNodesVerified()) {
    logger.warn(
      'Date shift: Auto correction: There are nodes pending for verification.',
    );
  }

  const phaseUpdates: DateShiftUpdates = {};
  const taskUpdates: DateShiftUpdates = {};

  Object.values(updates).forEach(({ type, dependencyFor, ...rest }) => {
    if (type === EntityType.Phase) {
      phaseUpdates[rest.id] = rest;
    } else if (type === EntityType.Task) {
      taskUpdates[rest.id] = rest;
    }
  });

  return [phaseUpdates, taskUpdates];
};

export const getDateShiftsForEntity = (
  phases: List,
  tasks: List,
  entity: Entity,
  options?: Options,
): [DateShiftUpdates, DateShiftUpdates] => {
  const updates: Updates = {
    [entity.id]: { ...entity },
  };
  const nodesLookup = { ...phases, ...tasks };
  const allowWeekend = options?.allowWeekend ?? false;
  const allowWeekendForEntityTree =
    allowWeekend ||
    (!!entity.startDate && isWeekEnd(entity.startDate)) ||
    (!!entity.dueDate && isWeekEnd(entity.dueDate));
  const alignParentWithChildren = options?.alignParentWithChildren;
  const alignPhaseWithTask = options?.alignPhaseWithTask;

  const phasesToBeProcessed: number[] = [];

  const getCurrentNode = (id) => ({
    ...nodesLookup[id],
  });

  const getUpdatedNode = (id) => ({
    ...nodesLookup[id],
    ...updates[id],
  });

  const updateNode = (id: number, nodeUpdates: Partial<Entity>) => {
    updates[id] = {
      ...(updates[id] || { id, type: nodesLookup[id].type }),
      ...nodeUpdates,
    };
  };

  const getAllChildNodes = (id: number): number[] => {
    const { children } = getUpdatedNode(id);

    return children && children.length > 0
      ? [
          ...children,
          ...children.reduce((result, childId) => {
            result.push(...getAllChildNodes(childId));

            return result;
          }, [] as number[]),
        ]
      : [];
  };

  const _addDays = (date, daysCount, _allowWeekend?: boolean) => {
    if (_allowWeekend) {
      return moment(date).add(daysCount, 'day').format(API_DATE_FORMAT);
    }

    let newDate = moment(date);
    const direction = daysCount < 0 ? -1 : 1;
    let _daysCount = Math.abs(daysCount);

    if ([0, 6].includes(moment(date).day())) {
      let tempNewDate = moment(newDate);

      while (
        _daysCount > 0 &&
        [0, 6].includes(tempNewDate.add(direction, 'day').day())
      ) {
        tempNewDate = moment(newDate);
        _daysCount -= 1;
        newDate = newDate.add(direction, 'day');
      }
    }

    while (_daysCount > 0) {
      const tempNewDate = moment(newDate);

      if (![0, 6].includes(tempNewDate.add(direction, 'day').day())) {
        _daysCount -= 1;
      }
      newDate = newDate.add(direction, 'day');
    }

    return newDate.format(API_DATE_FORMAT);
  };

  const _getCurrentDuration = (
    id: number,
    _startDate: MomentInput,
    _dueDate: MomentInput,
  ) => {
    if (allowWeekend) {
      return getDaysDiff(_dueDate, _startDate, allowWeekend);
    }
    const childNodes = getAllChildNodes(id);
    const countedWeekends = [] as MomentInput[];
    const blackListedWeekends = [] as MomentInput[];

    if (isWeekEnd(_startDate)) {
      if (!blackListedWeekends.includes(_startDate)) {
        blackListedWeekends.push(
          _startDate,
          formatDate(
            moment(_startDate).day() === 6
              ? moment(_startDate).add(1, 'day')
              : moment(_startDate).subtract(1, 'day'),
          ),
        );
      }
    }

    if (isWeekEnd(_dueDate)) {
      if (!blackListedWeekends.includes(_dueDate)) {
        blackListedWeekends.push(
          _dueDate,
          formatDate(
            moment(_dueDate).day() === 6
              ? moment(_dueDate).add(1, 'day')
              : moment(_dueDate).subtract(1, 'day'),
          ),
        );
      }
    }

    childNodes?.forEach((childId) => {
      const { startDate, dueDate } = getUpdatedNode(childId);

      if (
        isWeekEnd(startDate) &&
        !countedWeekends.includes(startDate) &&
        !blackListedWeekends.includes(startDate)
      ) {
        countedWeekends.push(
          startDate,
          formatDate(
            moment(startDate).day() === 6
              ? moment(startDate).add(1, 'day')
              : moment(startDate).subtract(1, 'day'),
          ),
        );
      }

      if (
        isWeekEnd(dueDate) &&
        !countedWeekends.includes(dueDate) &&
        !blackListedWeekends.includes(dueDate)
      ) {
        countedWeekends.push(
          dueDate,
          formatDate(
            moment(dueDate).day() === 6
              ? moment(dueDate).add(1, 'day')
              : moment(dueDate).subtract(1, 'day'),
          ),
        );
      }
    });

    return (
      getDuration(_startDate, _dueDate, allowWeekend) + countedWeekends.length
    );
  };

  const getDateDiffForEntity = (id: number): EntityDateDiff => {
    const { startDate: newStartDate, dueDate: newDueDate } = getUpdatedNode(id);
    const { startDate, dueDate } = getCurrentNode(id);

    const currentDuration = _getCurrentDuration(id, startDate, dueDate);
    const newDuration = getDuration(newStartDate, newDueDate, allowWeekend);

    return {
      startDate: allowWeekend
        ? getDateDiff(newStartDate, startDate)
        : getDaysDiff(newStartDate, startDate),
      dueDate: allowWeekend
        ? getDateDiff(newDueDate, dueDate)
        : getDaysDiff(newDueDate, dueDate),
      duration: newDuration - currentDuration,
    } as EntityDateDiff;
  };

  const getPhaseDates = (id: number): [MomentInput, MomentInput] => {
    const { startDate, dueDate, children } = getUpdatedNode(id);

    const [_startDate, _dueDate] = children?.reduce((result, childId) => {
      const [currentStartDate, currentDueDate] = result;
      let newStartDate = currentStartDate;
      let newDueDate = currentDueDate;
      const { startDate: childStartDate, dueDate: childDueDate } =
        getUpdatedNode(childId);

      if (
        !currentStartDate ||
        (childStartDate &&
          moment(childStartDate).isBefore(moment(currentStartDate)))
      ) {
        newStartDate = childStartDate;
      }
      if (
        !currentDueDate ||
        (childDueDate && moment(childDueDate).isAfter(moment(currentDueDate)))
      ) {
        newDueDate = childDueDate;
      }

      return [newStartDate, newDueDate];
    }, [] as unknown as [MomentInput, MomentInput]) || [
      moment(startDate).format(API_DATE_FORMAT),
      moment(dueDate).format(API_DATE_FORMAT),
    ];

    return [
      moment(_startDate).format(API_DATE_FORMAT),
      moment(_dueDate).format(API_DATE_FORMAT),
    ];
  };

  const postponeDependenciesFor = (_id: number) => {
    const { dueDate: nodeDueDate, dependencyFor, parent } = getUpdatedNode(_id);

    const parentNode = getUpdatedNode(parent);

    dependencyFor?.forEach((dependencyId) => {
      const {
        startDate: currentStartDate,
        dueDate: currentDueDate,
        parent: parentOfDependency,
      } = getCurrentNode(dependencyId);

      const { startDate } = getUpdatedNode(dependencyId);

      /*
      This block is added to let phase dependency take precedence over the task's dependency.

      This is added after backend changes went live where they check if the parents have dependency,
      if yes, then they will process them instead of processing the task dependency.

      */

      if (!parentNode.parent) {
        const parentOfDependencyNode = getUpdatedNode(parentOfDependency);

        if (!parentOfDependencyNode.parent && parentOfDependency) {
          if (
            parentNode.dependencyFor?.some((id) => id === parentOfDependency)
          ) {
            const _newStartDate = _addDays(parentNode.dueDate, 1, allowWeekend);
            const { startDate: _currentStartDate, dueDate: _currentDueDate } =
              getCurrentNode(parentOfDependency);

            const { startDate: _startDate } =
              getUpdatedNode(parentOfDependency);

            const duration = _getCurrentDuration(
              parentOfDependency,
              _currentStartDate,
              _currentDueDate,
            );

            updateNode(parentOfDependency, {
              startDate: moment(_newStartDate).format(API_DATE_FORMAT),
              dueDate: _addDays(_newStartDate, duration - 1, allowWeekend),
            });
            const shiftedBy = getDaysDiff(_newStartDate, _startDate);

            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return handleShift(parentOfDependency, shiftedBy);
          }
        }
      }

      if (moment(startDate).isAfter(nodeDueDate)) {
        return false;
      }
      const newStartDate = _addDays(nodeDueDate, 1, allowWeekend);
      const duration = _getCurrentDuration(
        dependencyId,
        currentStartDate,
        currentDueDate,
      );
      const shiftedBy = getDaysDiff(newStartDate, startDate);

      updateNode(dependencyId, {
        startDate: moment(newStartDate).format(API_DATE_FORMAT),
        dueDate: _addDays(newStartDate, duration - 1, allowWeekend),
      });

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return handleShift(dependencyId, shiftedBy);
    });
  };

  const preponeDependentsOn = (_id: number) => {
    const {
      startDate: nodeStartDate,
      dependentOn,
      parent,
    } = getUpdatedNode(_id);
    const parentNode = getUpdatedNode(parent);

    dependentOn?.forEach((dependencyId) => {
      const {
        startDate: currentStartDate,
        dueDate: currentDueDate,
        parent: parentOfDependency,
      } = getCurrentNode(dependencyId);

      const { dueDate } = getUpdatedNode(dependencyId);

      /*
      This block is added to let phase dependency take precedence over the task's dependency.

      This is added after backend changes went live where they check if the parents have dependency,
      if yes, then they will process them instead of processing the task dependency.
      */

      if (!parentNode.parent) {
        const parentOfDependencyNode = getUpdatedNode(parentOfDependency);

        if (!parentOfDependencyNode.parent && parentOfDependency) {
          if (parentNode.dependentOn?.some((id) => id === parentOfDependency)) {
            const _newDueDate = _addDays(
              parentNode.startDate,
              -1,
              allowWeekend,
            );

            const { startDate: _currentStartDate, dueDate: _currentDueDate } =
              getCurrentNode(parentOfDependency);

            const { dueDate: _dueDate } = getUpdatedNode(parentOfDependency);

            const duration = _getCurrentDuration(
              parentOfDependency,
              _currentStartDate,
              _currentDueDate,
            );

            const newShiftedBy = getDateDiff(_newDueDate, _dueDate);

            updateNode(parentOfDependency, {
              startDate: _addDays(_newDueDate, -(duration - 1), allowWeekend),
              dueDate: _newDueDate,
            });

            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            return handleShift(parentOfDependency, newShiftedBy, {
              processDependentsOn: true,
            });
          }
        }
      }

      if (moment(dueDate).isBefore(nodeStartDate)) {
        return false;
      }
      const newDueDate = _addDays(nodeStartDate, -1, allowWeekend);
      const duration = _getCurrentDuration(
        dependencyId,
        currentStartDate,
        currentDueDate,
      );
      const newShiftedBy = getDaysDiff(newDueDate, dueDate);

      updateNode(dependencyId, {
        startDate: _addDays(newDueDate, -(duration - 1), allowWeekend),
        dueDate: newDueDate,
      });

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return handleShift(dependencyId, newShiftedBy, {
        processDependentsOn: true,
      });
    });
  };

  const getPhaseUpdates = (_phaseId) => {
    const [newStartDate, newDueDate] = getPhaseDates(_phaseId);
    const parentNode = getUpdatedNode(_phaseId);
    const _updates = {} as Partial<Entity>;

    if (!moment(parentNode.startDate).isSame(newStartDate)) {
      _updates.startDate = moment(newStartDate).format(API_DATE_FORMAT);
    }
    if (!moment(parentNode.dueDate).isSame(newDueDate)) {
      _updates.dueDate = moment(newDueDate).format(API_DATE_FORMAT);
    }

    return _updates;
  };

  const handleShift = (
    id: number,
    shiftedBy: number,
    _options?: HandleShiftOptions,
  ) => {
    const processDependentsOn = !_options || _options.processDependentsOn;
    const _allowWeekend =
      id === entity.id ? allowWeekendForEntityTree : allowWeekend;

    (function processChildren(_id: number) {
      const { startDate: currentStartDate } = getCurrentNode(_id);
      const { startDate: newStartDate, children } = getUpdatedNode(_id);
      const sortedChildNodes = children?.map(getCurrentNode)?.sort((a, b) => {
        return moment(a.startDate).diff(moment(b.startDate));
      });
      let lastPosition = 0;
      const childrenToBeProcessed: number[] = [];

      sortedChildNodes?.forEach((child, index) => {
        const handler = allowWeekend ? getDateDiff : getDaysDiff;
        const position =
          handler(
            child.startDate,
            index > 0
              ? sortedChildNodes[index - 1].startDate
              : currentStartDate,
          ) + lastPosition;

        lastPosition = position;
        let newChildStartDate = _addDays(newStartDate, position, _allowWeekend);

        const checkIfCurrentWeekendIsAllowed =
          (moment(newChildStartDate).format('dddd') === 'Saturday' &&
            moment(newStartDate).format('dddd') === 'Saturday' &&
            formatDate(newChildStartDate) === newStartDate) ||
          (moment(newStartDate).format('dddd') === 'Saturday' &&
            moment(newStartDate).add(1, 'day')) === moment(newChildStartDate) ||
          allowWeekend;

        if (!checkIfCurrentWeekendIsAllowed) {
          newChildStartDate = _addDays(
            newStartDate,
            position,
            checkIfCurrentWeekendIsAllowed,
          );
        }

        if (moment(newChildStartDate).isSame(moment(child.startDate))) {
          return;
        }
        const duration = _getCurrentDuration(
          child.id,
          child.startDate,
          child.dueDate,
        );
        let newChildDueDate = _addDays(
          newChildStartDate,
          duration - 1,
          checkIfCurrentWeekendIsAllowed,
        );

        /**
         * Defensive check to keep child within parent boundaries
         */
        const { startDate: parentStartDate, dueDate: parentDueDate } =
          getUpdatedNode(child.parent);

        if (moment(newChildStartDate).isBefore(parentStartDate)) {
          newChildStartDate = moment(parentStartDate).format(API_DATE_FORMAT);
          newChildDueDate = _addDays(
            newChildStartDate,
            duration - 1,
            checkIfCurrentWeekendIsAllowed,
          );

          if (moment(newChildDueDate).isAfter(moment(parentDueDate))) {
            newChildDueDate = moment(parentDueDate).format(API_DATE_FORMAT);
          }
        } else if (moment(newChildDueDate).isAfter(parentDueDate)) {
          newChildDueDate = moment(parentDueDate).format(API_DATE_FORMAT);
          newChildStartDate = _addDays(
            newChildDueDate,
            -(duration - 1),
            checkIfCurrentWeekendIsAllowed,
          );

          if (moment(newChildStartDate).isBefore(moment(parentStartDate))) {
            newChildStartDate = moment(parentStartDate).format(API_DATE_FORMAT);
          }
        }

        updateNode(child.id, {
          startDate: newChildStartDate,
          dueDate: newChildDueDate,
        });

        childrenToBeProcessed.push(child.id);
      });
      /**
       * Process node's dependencies
       */
      sortedChildNodes?.forEach((child) => {
        const { startDate: _newStartDate } = getUpdatedNode(child.id);
        const _shiftedBy = allowWeekend
          ? getDateDiff(_newStartDate, child.startDate)
          : getDaysDiff(_newStartDate, child.startDate);

        if (_shiftedBy > 0) {
          postponeDependenciesFor(child.id);
        } else if (shiftedBy < 0) {
          if (processDependentsOn) {
            preponeDependentsOn(child.id);
          }
        }
      });
      /** Process node's children */
      childrenToBeProcessed.forEach(processChildren);
    })(id);

    const parentIds = (function checkParent(_id: number): number[] {
      const { parent, startDate, dueDate } = getUpdatedNode(_id);

      if (!parent) {
        return [];
      }
      const parentNode = getUpdatedNode(parent);
      const isPhase = !parentNode.parent;
      let _updates = {} as Partial<Entity>;

      if (alignParentWithChildren || alignPhaseWithTask) {
        if (isPhase) {
          _updates = getPhaseUpdates(parent);
          phasesToBeProcessed.push(parent);
        } else if (alignParentWithChildren) {
          const [newStartDate, newDueDate] = getPhaseDates(parent);

          if (!moment(parentNode.startDate).isSame(newStartDate)) {
            _updates.startDate = moment(newStartDate).format(API_DATE_FORMAT);
          }
          if (!moment(parentNode.dueDate).isSame(newDueDate)) {
            _updates.dueDate = moment(newDueDate).format(API_DATE_FORMAT);
          }
        } else {
          if (
            shiftedBy > 0 &&
            moment(parentNode.dueDate).isBefore(moment(dueDate))
          ) {
            _updates.dueDate = dueDate;
          }

          if (
            shiftedBy < 0 &&
            moment(parentNode.startDate).isAfter(moment(startDate))
          ) {
            _updates.startDate = startDate;
          }
        }
      } else {
        if (
          shiftedBy > 0 &&
          moment(parentNode.dueDate).isBefore(moment(dueDate))
        ) {
          _updates.dueDate = dueDate;
        }

        if (
          shiftedBy < 0 &&
          moment(parentNode.startDate).isAfter(moment(startDate))
        ) {
          _updates.startDate = startDate;
        }
      }
      if (!isEmpty(_updates)) {
        updateNode(parent, _updates);

        return [parent, ...checkParent(parent)];
      }

      return [];
    })(id);

    /**
     * Process dependencies
     */
    // const updatedNodes = [id, ...childNodeIds, ...parentIds];
    const updatedNodes = [...parentIds, id];

    if (shiftedBy > 0 || shiftedBy === 0) {
      updatedNodes.forEach(postponeDependenciesFor);
    } else if (shiftedBy < 0) {
      updatedNodes.forEach((_id) => {
        // if (processDependenciesFor) {
        //   preponeDependenciesFor(_id, shiftedBy, updatedNodes);
        // }
        if (processDependentsOn) {
          // preponeDependentsOn(_id, shiftedBy, updatedNodes);
          preponeDependentsOn(_id);
        }
      });
    }
  };

  const handleResize = (id: number, diff: EntityDateDiff) => {
    const { startDate: startDiff, dueDate: endDiff } = diff;
    const { startDate: nodeStartDate, dueDate: nodeDueDate } =
      getUpdatedNode(id);

    const handleStartDatePrepone = () => {
      // Check Parent
      const parentIds = (function checkParent(_id: number): number[] {
        const { parent, startDate } = getUpdatedNode(_id);

        if (!parent) {
          return [];
        }
        const parentNode = getUpdatedNode(parent);
        const isPhase = !parentNode.parent;
        let _updates = {} as Partial<Entity>;

        if (alignParentWithChildren || alignPhaseWithTask) {
          if (isPhase) {
            _updates = getPhaseUpdates(parent);
            phasesToBeProcessed.push(parent);
          } else if (alignParentWithChildren) {
            const [newStartDate, newDueDate] = getPhaseDates(parent);

            if (!moment(parentNode.startDate).isSame(newStartDate)) {
              _updates.startDate = moment(newStartDate).format(API_DATE_FORMAT);
            }
            if (!moment(parentNode.dueDate).isSame(newDueDate)) {
              _updates.dueDate = moment(newDueDate).format(API_DATE_FORMAT);
            }
          } else if (moment(parentNode.startDate).isAfter(moment(startDate))) {
            _updates.startDate = startDate;
          }
        } else if (moment(parentNode.startDate).isAfter(moment(startDate))) {
          _updates.startDate = startDate;
        }
        if (!isEmpty(_updates)) {
          updateNode(parent, _updates);

          return [parent, ...checkParent(parent)];
        }

        return [];
      })(id);

      // Check Dependent On
      [id, ...parentIds].forEach((_id) => {
        // preponeDependentsOn(_id, startDiff);
        preponeDependentsOn(_id);
      });

      // Check Children (for phase, not parent task)?
    };

    const handleStartDatePostpone = () => {
      // Move children
      const childNodeIds = getAllChildNodes(id);

      childNodeIds.forEach((childId) => {
        const { startDate: currentStartDate, dueDate: currentDueDate } =
          getCurrentNode(childId);
        const child = getUpdatedNode(childId);

        if (moment(child.startDate).isSameOrAfter(moment(nodeStartDate))) {
          return true;
        }
        const newStartDate = nodeStartDate;
        const _updates = { startDate: newStartDate } as Partial<Entity>;

        const duration = _getCurrentDuration(
          childId,
          currentStartDate,
          currentDueDate,
        );

        let newDueDate: MomentInput = _addDays(
          newStartDate,
          duration - 1,
          allowWeekend,
        );

        if (moment(newDueDate).isAfter(moment(nodeDueDate))) {
          newDueDate = nodeDueDate;
        }
        _updates.dueDate = newDueDate;
        updateNode(childId, _updates);

        return postponeDependenciesFor(childId);
      });

      // Move phase not parent task
      /**
       * Postponse phase start date, not parent task
       * since parent task can have additional duration unlike a phase
       *
       * Latest product call: allow phase to have additional duration
       * similar to parent task, based on the config `alignParentWithChildren`
       */
      const { parent } = getUpdatedNode(id);

      if (!parent) {
        return;
      }
      const parentNode = getUpdatedNode(parent);
      const isPhase = !parentNode.parent;
      let _updates = {} as Partial<Entity>;

      if (!alignParentWithChildren && !alignPhaseWithTask) {
        return;
      }
      if (isPhase) {
        _updates = getPhaseUpdates(parent);
        phasesToBeProcessed.push(parent);
      } else if (alignParentWithChildren) {
        const [newStartDate, newDueDate] = getPhaseDates(parent);

        if (!moment(parentNode.startDate).isSame(newStartDate)) {
          _updates.startDate = moment(newStartDate).format(API_DATE_FORMAT);
        }
        if (!moment(parentNode.dueDate).isSame(newDueDate)) {
          _updates.dueDate = moment(newDueDate).format(API_DATE_FORMAT);
        }
      }

      if (!isEmpty(_updates)) {
        updateNode(parent, _updates);
      }

      // Check due date overlap?
    };

    const handleDueDatePrepone = () => {
      // Check children
      const childNodeIds = getAllChildNodes(id);
      const updatedChildIds = [] as number[];

      childNodeIds.forEach((childId) => {
        const { startDate: currentStartDate, dueDate: currentDueDate } =
          getCurrentNode(childId);
        const child = getUpdatedNode(childId);

        if (moment(child.dueDate).isSameOrBefore(moment(nodeDueDate))) {
          return;
        }
        const newDueDate = nodeDueDate;
        const _updates = { dueDate: newDueDate } as Partial<Entity>;

        const duration = _getCurrentDuration(
          childId,
          currentStartDate,
          currentDueDate,
        );

        let newStartDate: MomentInput = _addDays(
          newDueDate,
          -(duration - 1),
          allowWeekend,
        );

        if (moment(newStartDate).isBefore(moment(nodeStartDate))) {
          newStartDate = nodeStartDate;
        }
        _updates.startDate = newStartDate;

        updateNode(childId, _updates);
        updatedChildIds.push(childId);
      });

      [id, ...updatedChildIds].forEach((_id) => preponeDependentsOn(_id));

      /**
       * Prepone phase due date, not parent task
       * since parent task can have additional duration unlike a phase
       *
       * Latest product call: allow phase to have additional duration
       * similar to parent task, based on the config `alignParentWithChildren`
       */
      const { parent } = getUpdatedNode(id);

      if (!parent) {
        return;
      }
      const parentNode = getUpdatedNode(parent);
      const isPhase = !parentNode.parent;
      let _updates = {} as Partial<Entity>;

      if (alignParentWithChildren || alignPhaseWithTask) {
        if (isPhase) {
          _updates = getPhaseUpdates(parent);
          phasesToBeProcessed.push(parent);
        } else if (alignParentWithChildren) {
          const [newStartDate, newDueDate] = getPhaseDates(parent);

          if (!moment(parentNode.startDate).isSame(newStartDate)) {
            _updates.startDate = moment(newStartDate).format(API_DATE_FORMAT);
          }
          if (!moment(parentNode.dueDate).isSame(newDueDate)) {
            _updates.dueDate = moment(newDueDate).format(API_DATE_FORMAT);
          }
        }

        if (!isEmpty(_updates)) {
          updateNode(parent, _updates);
        }
      }

      // Check start date overlap?
    };

    const handleDueDatePostpone = () => {
      // Move Parent
      const parentIds = (function checkParent(_id: number): number[] {
        const { parent, dueDate } = getUpdatedNode(_id);

        if (!parent) {
          return [];
        }
        const parentNode = getUpdatedNode(parent);
        const isPhase = !parentNode.parent;
        let _updates = {} as Partial<Entity>;

        if (alignParentWithChildren || alignPhaseWithTask) {
          if (isPhase) {
            _updates = getPhaseUpdates(parent);
            phasesToBeProcessed.push(parent);
          } else if (alignParentWithChildren) {
            const [newStartDate, newDueDate] = getPhaseDates(parent);

            if (!moment(parentNode.startDate).isSame(newStartDate)) {
              _updates.startDate = moment(newStartDate).format(API_DATE_FORMAT);
            }
            if (!moment(parentNode.dueDate).isSame(newDueDate)) {
              _updates.dueDate = moment(newDueDate).format(API_DATE_FORMAT);
            }
          } else if (moment(parentNode.dueDate).isBefore(moment(dueDate))) {
            _updates.dueDate = dueDate;
          }
        } else if (moment(parentNode.dueDate).isBefore(moment(dueDate))) {
          _updates.dueDate = dueDate;
        }
        if (!isEmpty(_updates)) {
          updateNode(parent, _updates);

          return [parent, ...checkParent(parent)];
        }

        return [];
      })(id);

      // Check Dependencies For
      [id, ...parentIds].forEach((_id) => postponeDependenciesFor(_id));

      // Check Children (for phase, not parent task)?
    };

    if (startDiff < 0) {
      handleStartDatePrepone();
    } else if (startDiff > 0) {
      handleStartDatePostpone();
    }

    if (endDiff < 0) {
      handleDueDatePrepone();
    } else if (endDiff > 0) {
      handleDueDatePostpone();
    }
  };

  // if (isWeekEnd(entity.dueDate)) {
  //   const { startDate, dueDate } = getUpdatedNode(entity.id);
  //   const duration = _getCurrentDuration(entity.id, startDate, dueDate);

  //   updateNode(entity.id, {
  //     dueDate: _addDays(startDate, duration - 1),
  //   });
  // }
  const diff = getDateDiffForEntity(entity.id);

  if (diff.duration === 0) {
    handleShift(entity.id, diff.startDate);
  } else {
    handleResize(entity.id, diff);
  }

  phasesToBeProcessed.forEach((_phaseId) => {
    const _updates = getPhaseUpdates(_phaseId);

    if (!isEmpty(_updates)) {
      updateNode(_phaseId, _updates);
    }
  });

  const phaseUpdates: DateShiftUpdates = {};
  const taskUpdates: DateShiftUpdates = {};

  Object.values(updates).forEach(({ type, ...rest }) => {
    if (type === EntityType.Phase) {
      phaseUpdates[rest.id] = rest;
    } else if (type === EntityType.Task) {
      taskUpdates[rest.id] = rest;
    }
  });

  return [phaseUpdates, taskUpdates];
};
